/**
 * Shopify Authentication Module
 * Handles customer login, registration, password recovery, and logout
 */

class ShopifyAuth {
  constructor(apiClient) {
    this.api = apiClient;
    this.utils = window.ShopifyUtils;
  }

  /**
   * Customer login mutation
   */
  LOGIN_MUTATION = `
    mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
      customerAccessTokenCreate(input: $input) {
        customerAccessToken {
          accessToken
          expiresAt
        }
        customerUserErrors {
          code
          field
          message
        }
      }
    }
  `;

  /**
   * Customer registration mutation
   */
  REGISTER_MUTATION = `
    mutation customerCreate($input: CustomerCreateInput!) {
      customerCreate(input: $input) {
        customer {
          id
        }
        customerUserErrors {
          code
          field
          message
        }
      }
    }
  `;

  /**
   * Password recovery mutation
   */
  RECOVER_MUTATION = `
    mutation customerRecover($email: String!) {
      customerRecover(email: $email) {
        customerUserErrors {
          code
          field
          message
        }
      }
    }
  `;

  /**
   * Password reset mutation
   */
  RESET_MUTATION = `
    mutation customerReset($id: ID!, $input: CustomerResetInput!) {
      customerReset(id: $id, input: $input) {
        customer {
          id
        }
        customerAccessToken {
          accessToken
          expiresAt
        }
        customerUserErrors {
          code
          field
          message
        }
      }
    }
  `;

  /**
   * Token renewal mutation
   * Used to extend token expiration before it expires
   */
  RENEW_TOKEN_MUTATION = `
    mutation customerAccessTokenRenew($customerAccessToken: String!) {
      customerAccessTokenRenew(customerAccessToken: $customerAccessToken) {
        customerAccessToken {
          accessToken
          expiresAt
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  /**
   * Login with email and password
   * @param {string} email - Customer email
   * @param {string} password - Customer password
   * @returns {Promise<Object>}
   */
  async login(email, password) {
    try {
      const variables = {
        input: {
          email: email,
          password: password
        }
      };

      const response = await this.api.mutate(this.LOGIN_MUTATION, variables);

      if (this.utils.hasErrors(response)) {
        const errorMessage = this.utils.formatErrorMessage(response);
        throw new Error(errorMessage);
      }

      const tokenData = response.data.customerAccessTokenCreate.customerAccessToken;

      if (tokenData) {
        // Store token in localStorage
        this.utils.setCustomerToken(tokenData.accessToken, tokenData.expiresAt);

        // Sync cart with customer identity for authenticated checkout
        this.syncCartAfterLogin();

        return {
          success: true,
          accessToken: tokenData.accessToken,
          expiresAt: tokenData.expiresAt
        };
      }

      throw new Error('Login failed. Please check your credentials.');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Sync cart with customer identity after login
   * This ensures the cart is associated with the customer for authenticated checkout
   */
  async syncCartAfterLogin() {
    try {
      // Use CartSync if available
      if (window.CartSync && typeof window.CartSync.handleCustomerLogin === 'function') {
        await window.CartSync.handleCustomerLogin();
      }
    } catch (error) {
      console.warn('Failed to sync cart after login:', error);
      // Don't throw - cart sync failure shouldn't break login
    }
  }

  /**
   * Register a new customer
   * @param {Object} userData - Customer data
   * @param {string} userData.email - Customer email
   * @param {string} userData.password - Customer password
   * @param {string} userData.firstName - Customer first name
   * @param {string} userData.lastName - Customer last name
   * @returns {Promise<Object>}
   */
  async register(userData) {
    try {
      const variables = {
        input: {
          email: userData.email,
          password: userData.password,
          firstName: userData.firstName,
          lastName: userData.lastName
        }
      };

      const response = await this.api.mutate(this.REGISTER_MUTATION, variables);

      if (this.utils.hasErrors(response)) {
        const errorMessage = this.utils.formatErrorMessage(response);
        throw new Error(errorMessage);
      }

      const customer = response.data.customerCreate.customer;

      if (customer) {
        // Automatically login after registration
        try {
          const loginResult = await this.login(userData.email, userData.password);
          // Note: login() already calls syncCartAfterLogin()
          return {
            success: true,
            customer: customer,
            accessToken: loginResult.accessToken,
            expiresAt: loginResult.expiresAt
          };
        } catch (loginError) {
          // Registration succeeded but auto-login failed
          return {
            success: true,
            customer: customer,
            requiresLogin: true
          };
        }
      }

      throw new Error('Registration failed. Please try again.');
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Request password recovery email
   * @param {string} email - Customer email
   * @returns {Promise<Object>}
   */
  async recoverPassword(email) {
    try {
      const variables = {
        email: email
      };

      const response = await this.api.mutate(this.RECOVER_MUTATION, variables);

      if (this.utils.hasErrors(response)) {
        const errorMessage = this.utils.formatErrorMessage(response);
        throw new Error(errorMessage);
      }

      // Check for functional errors (e.g. invalid email format)
      const data = response.data?.customerRecover;
      if (data?.customerUserErrors?.length > 0) {
        throw new Error(data.customerUserErrors[0].message);
      }

      return {
        success: true,
        message: 'Successfully sent email.'
      };
    } catch (error) {
      console.error('Password recovery error:', error);
      throw error;
    }
  }

  /**
   * Reset customer password using reset token
   * @param {Object} resetData - Reset data
   * @param {string} resetData.id - Customer ID (numeric or GID format)
   * @param {string} resetData.token - Reset token from email
   * @param {string} resetData.password - New password
   * @returns {Promise<Object>}
   */
  async resetPassword(resetData) {
    try {
      if (!resetData.id || !resetData.token || !resetData.password) {
        throw new Error('Missing required reset data: id, token, and password are required.');
      }

      // Convert numeric ID to GID format if needed
      let customerId = resetData.id;
      if (!customerId.startsWith('gid://')) {
        customerId = `gid://shopify/Customer/${customerId}`;
      }

      const variables = {
        id: customerId,
        input: {
          resetToken: resetData.token,
          password: resetData.password
        }
      };

      const response = await this.api.mutate(this.RESET_MUTATION, variables);

      if (this.utils.hasErrors(response)) {
        const errorMessage = this.utils.formatErrorMessage(response);
        throw new Error(errorMessage);
      }

      const result = response.data.customerReset;

      if (result.customerUserErrors && result.customerUserErrors.length > 0) {
        const errorMessage = result.customerUserErrors[0].message;
        throw new Error(errorMessage);
      }

      // If reset successful and access token provided, store it and sync cart
      if (result.customerAccessToken) {
        this.utils.setCustomerToken(
          result.customerAccessToken.accessToken,
          result.customerAccessToken.expiresAt
        );

        // Sync cart with customer identity for authenticated checkout
        this.syncCartAfterLogin();
      }

      return {
        success: true,
        customer: result.customer,
        accessToken: result.customerAccessToken?.accessToken,
        expiresAt: result.customerAccessToken?.expiresAt
      };
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  /**
   * Logout - clear customer token and handle cart
   * @param {boolean} clearCart - Whether to clear the cart entirely (default: false)
   */
  logout(clearCart = false) {
    this.utils.clearCustomerToken();

    // Handle cart on logout
    this.handleCartOnLogout(clearCart);

    return {
      success: true,
      message: 'Logged out successfully'
    };
  }

  /**
   * Handle cart state on logout
   * @param {boolean} clearCart - Whether to clear the cart entirely
   */
  handleCartOnLogout(clearCart = false) {
    try {
      // Use CartSync if available
      if (window.CartSync && typeof window.CartSync.handleCustomerLogout === 'function') {
        window.CartSync.handleCustomerLogout(clearCart);
      }
    } catch (error) {
      console.warn('Failed to handle cart on logout:', error);
      // Don't throw - cart handling failure shouldn't break logout
    }
  }

  /**
   * Check if user is logged in
   * Also triggers token refresh if token is close to expiring
   * @returns {boolean}
   */
  isLoggedIn() {
    const token = this.utils.getCustomerToken();
    if (token) {
      // Check if token needs refresh (async, don't block)
      this.refreshTokenIfNeeded().catch(err => {
        console.warn('Token refresh check failed:', err);
      });
    }
    return token !== null;
  }

  /**
   * Get current customer access token
   * @returns {string|null}
   */
  getAccessToken() {
    return this.utils.getCustomerToken();
  }

  /**
   * Check if token is close to expiring and refresh if needed
   * Refreshes token if less than 7 days until expiration
   * @returns {Promise<Object>}
   */
  async refreshTokenIfNeeded() {
    try {
      const token = localStorage.getItem('shopifyCustomerAccessToken');
      const expiresAt = localStorage.getItem('shopifyCustomerAccessTokenExpires');

      if (!token || !expiresAt) {
        return { refreshed: false, reason: 'No token' };
      }

      const expirationDate = new Date(expiresAt);
      const now = new Date();
      const daysUntilExpiry = (expirationDate - now) / (1000 * 60 * 60 * 24);

      // Only refresh if less than 7 days until expiry
      if (daysUntilExpiry > 7) {
        return { refreshed: false, reason: 'Token still valid', daysRemaining: daysUntilExpiry };
      }


      const result = await this.renewToken(token);
      return result;
    } catch (error) {
      console.error('Error checking token refresh:', error);
      return { refreshed: false, error: error.message };
    }
  }

  /**
   * Renew customer access token
   * @param {string} currentToken - Current access token to renew
   * @returns {Promise<Object>}
   */
  async renewToken(currentToken) {
    try {
      const variables = {
        customerAccessToken: currentToken
      };

      const response = await this.api.mutate(this.RENEW_TOKEN_MUTATION, variables);

      // Check for errors
      const userErrors = response.data?.customerAccessTokenRenew?.userErrors;
      if (userErrors && userErrors.length > 0) {
        const errorMessage = userErrors[0].message;
        console.warn('Token renewal failed:', errorMessage);
        return { refreshed: false, error: errorMessage };
      }

      const tokenData = response.data?.customerAccessTokenRenew?.customerAccessToken;

      if (tokenData && tokenData.accessToken) {
        // Store the renewed token
        this.utils.setCustomerToken(tokenData.accessToken, tokenData.expiresAt);

        return {
          refreshed: true,
          accessToken: tokenData.accessToken,
          expiresAt: tokenData.expiresAt
        };
      }

      return { refreshed: false, reason: 'No token in response' };
    } catch (error) {
      console.error('Token renewal error:', error);
      return { refreshed: false, error: error.message };
    }
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.ShopifyAuth = ShopifyAuth;
}

